import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Eye } from 'lucide-react'

import { api } from '@/lib/api'
import { formatRelativeTime } from '@/lib/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AuditFilters, type AuditFilterValues } from '@/components/AuditFilters'
import { PayloadDialog } from '@/components/PayloadDialog'
import { useAuthStore } from '@/stores/auth'
import type {
  AuditLogEntry,
  AuditLogListResponse,
  AuditDatePreset,
} from '@sdr-agend/admin-schemas'

const EMPTY_FILTERS: AuditFilterValues = {
  action: '',
  resourceType: '',
  actorUserId: '',
  preset: '',
  from: '',
  to: '',
}

/**
 * `/admin/audit-log` — read-only viewer over `audit_log` (Story 3.17).
 *
 * Decisions:
 *   - Filters deep-linked via query string (§AC #7) so admins can share URLs.
 *   - "Carregar mais" cursor pagination (§AC #5) — no offset.
 *   - RBAC: admin + manager only (§AC #8 + §Decision 3); other roles see
 *     "Acesso restrito" banner. Server-side returns 403 in those cases.
 */
export function AuditLogPage(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const canView = role === 'admin' || role === 'manager'
  const [searchParams, setSearchParams] = useSearchParams()
  const [previewPayload, setPreviewPayload] = useState<{
    open: boolean
    payload: unknown
    title: string
  }>({ open: false, payload: null, title: '' })

  // Initialize filter state from query string (deep-link support, §AC #7).
  // Lazy state init reads query string once on mount; subsequent filter
  // mutations sync the query string via the effect below.
  const [filters, setFilters] = useState<AuditFilterValues>(() => ({
    action: searchParams.get('action') ?? '',
    resourceType: searchParams.get('resourceType') ?? '',
    actorUserId: searchParams.get('actorUserId') ?? '',
    preset: (searchParams.get('preset') as AuditDatePreset | null) ?? '',
    from: searchParams.get('from') ?? '',
    to: searchParams.get('to') ?? '',
  }))

  // `pages` is the keyset chain — each subsequent fetch appends to the list.
  const [pages, setPages] = useState<AuditLogEntry[][]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasNext, setHasNext] = useState<boolean>(false)

  // Reset pagination when filters change.
  useEffect(() => {
    setPages([])
    setCursor(null)
    setHasNext(false)
  }, [filters])

  // Sync filters → query string for deep-linking.
  useEffect(() => {
    const params = new URLSearchParams()
    if (filters.action !== '') params.set('action', filters.action)
    if (filters.resourceType !== '') params.set('resourceType', filters.resourceType)
    if (filters.actorUserId !== '') params.set('actorUserId', filters.actorUserId)
    if (filters.preset !== '') params.set('preset', filters.preset)
    if (filters.from !== '') params.set('from', filters.from)
    if (filters.to !== '') params.set('to', filters.to)
    setSearchParams(params, { replace: true })
  }, [filters, setSearchParams])

  const queryKey = useMemo(
    () => ['admin-audit-log', filters, cursor] as const,
    [filters, cursor],
  )

  const { data, isFetching, isError, refetch } = useQuery<AuditLogListResponse>({
    queryKey,
    enabled: canView,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.action !== '') params.set('action', filters.action)
      if (filters.resourceType !== '') params.set('resourceType', filters.resourceType)
      if (filters.actorUserId !== '') params.set('actorUserId', filters.actorUserId)
      if (filters.from !== '') params.set('from', filters.from)
      if (filters.to !== '') params.set('to', filters.to)
      if (cursor !== null) params.set('cursor', cursor)
      const qs = params.toString()
      return api<AuditLogListResponse>(
        `/admin/audit-log${qs === '' ? '' : `?${qs}`}`,
      )
    },
  })

  // Append fetched page to local list when results arrive.
  useEffect(() => {
    if (data === undefined) return
    setPages((prev) => {
      // Avoid double-append: only push when this batch isn't the last one
      // we accepted. We detect dup-by-id on the first row.
      const firstId = data.items[0]?.id
      if (
        firstId !== undefined &&
        prev[prev.length - 1]?.[0]?.id === firstId
      ) {
        return prev
      }
      return [...prev, data.items]
    })
    setHasNext(data.nextCursor !== null)
  }, [data])

  function loadMore(): void {
    if (data?.nextCursor === undefined || data.nextCursor === null) return
    setCursor(data.nextCursor)
    void refetch()
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">Audit Log</h1>
        <Card className="border-destructive/40 bg-destructive/10 p-4">
          <p className="text-sm">
            Acesso restrito. Apenas <strong>admin</strong> e <strong>manager</strong> podem
            visualizar o audit log.
          </p>
        </Card>
      </div>
    )
  }

  const allItems: AuditLogEntry[] = pages.flat()

  // Build the actor combobox from rows we've already seen + the current
  // selection (keeps a stable list as the user paginates).
  const actorOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const item of allItems) {
      if (item.actorUserId !== null && item.actorEmail !== null) {
        map.set(item.actorUserId, item.actorEmail)
      }
    }
    return Array.from(map.entries()).map(([id, email]) => ({ id, email }))
  }, [allItems])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit Log</h1>
        <p className="text-sm text-muted-foreground">
          Trilha de auditoria de mudanças em prompts, campanhas, sellers, settings, etc.
        </p>
      </div>

      <AuditFilters values={filters} onChange={setFilters} actors={actorOptions} />

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Quando</TableHead>
              <TableHead>Ação</TableHead>
              <TableHead>Recurso</TableHead>
              <TableHead>Ator</TableHead>
              <TableHead>Correlation</TableHead>
              <TableHead className="w-20">Payload</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isFetching && allItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-destructive">
                  Erro ao carregar audit log.
                </TableCell>
              </TableRow>
            )}
            {!isFetching && !isError && allItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  Nenhum evento no período/filtros aplicados.
                </TableCell>
              </TableRow>
            )}
            {allItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell title={item.createdAt}>{formatRelativeTime(item.createdAt)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-mono text-xs">
                    {item.action}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  <div>{item.resourceType}</div>
                  {item.resourceId !== null && (
                    <div className="text-muted-foreground">{truncate(item.resourceId, 12)}</div>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {item.actorEmail ?? (item.actorUserId === null ? 'system' : item.actorUserId)}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {item.correlationId === null ? '—' : truncate(item.correlationId, 12)}
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setPreviewPayload({
                        open: true,
                        payload: item.payload,
                        title: `${item.action} — ${item.resourceType}`,
                      })
                    }
                    aria-label={`Ver payload de ${item.action}`}
                  >
                    <Eye className="h-4 w-4" aria-hidden="true" />
                    Ver
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {hasNext && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={loadMore}
            disabled={isFetching}
          >
            {isFetching ? 'Carregando…' : 'Carregar mais'}
          </Button>
        </div>
      )}

      <PayloadDialog
        open={previewPayload.open}
        onOpenChange={(open) => setPreviewPayload((s) => ({ ...s, open }))}
        payload={previewPayload.payload}
        title={previewPayload.title}
      />
    </div>
  )
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n)}…`
}

void EMPTY_FILTERS

export default AuditLogPage
